export type LakeComoPageStudioCapability = "page" | "faq" | "guides" | "editorialReview";

export type LakeComoPageFamily = "hub" | "route" | "ferries" | "boat" | "task" | "place";
export type LakeComoPageKey = string;

export type LakeComoPageStudioDefinition = {
  pageKey: string;
  title: string;
  defaultImageQuery: string;
  capabilities: LakeComoPageStudioCapability[];
  family: LakeComoPageFamily;
  /** Canonical English route. Locale paths are derived centrally, never copied per locale. */
  canonicalPath: string;
  sourceLocale: "en";
  localizable: boolean;
  /** A page can be translated in storage before its existing renderer has been
   * adapted to consume the locale projection. Prevent premature publication. */
  localizedRendererReady: boolean;
  publicLocales: readonly string[];
  sitemapEligible: boolean;
  structuredDataType: "WebPage" | "FAQPage" | "Place" | "TouristAttraction";
  requiredTranslationSegments: readonly string[];
  optionalTranslationSegments: readonly string[];
  faqCapability: boolean;
  editorialReviewCapability: boolean;
  commercialCapability: boolean;
};

const defaultPublicLocales = ["en"] as const;
const baseTranslationSegments = ["metadata.title", "metadata.description"] as const;

const gettingAround: LakeComoPageStudioDefinition = {
  pageKey: "lake-como/getting-around",
  title: "Getting around Lake Como",
  defaultImageQuery: "Lake Como ferry and mountains Italy",
  capabilities: ["page", "editorialReview", "faq", "guides"],
  family: "hub",
  canonicalPath: "/lake-como/getting-around",
  sourceLocale: "en",
  localizable: true,
  localizedRendererReady: true,
  publicLocales: ["en", "it"],
  sitemapEligible: true,
  structuredDataType: "FAQPage",
  requiredTranslationSegments: ["hero.title", "hero.subtitle", ...baseTranslationSegments],
  optionalTranslationSegments: ["editorial_review.heading", "editorial_review.intro"],
  faqCapability: true,
  editorialReviewCapability: true,
  commercialCapability: true,
};

const routeLikePaths = new Set([
  "lake-como/airports",
  "lake-como/driving-and-rentals",
  "lake-como/getting-around-without-a-car",
  "lake-como/milan-to-varenna",
  "lake-como/milan-to-como",
  "lake-como/milan-to-bellagio",
  "lake-como/milan-to-menaggio",
  "lake-como/bellagio-to-milan",
  "lake-como/bellagio-to-varenna-ferry",
  "lake-como/varenna-to-bellagio-ferry",
  "lake-como/bellagio-to-menaggio-ferry",
  "lake-como/menaggio-to-bellagio-ferry",
  "lake-como/varenna-to-menaggio-ferry",
  "lake-como/menaggio-to-varenna-ferry",
  "lake-como/lecco-to-bellagio-ferry",
  "lake-como/bellagio-to-lecco-ferry",
  "lake-como/como-to-bellagio",
  "lake-como/como-to-varenna-ferry",
  "lake-como/varenna-to-como-ferry",
  "lake-como/como-to-menaggio-ferry",
  "lake-como/menaggio-to-como-ferry",
  "lake-como/como-to-lenno-ferry",
  "lake-como/lenno-to-como-ferry",
  "lake-como/como-to-tremezzo-ferry",
  "lake-como/tremezzo-to-como-ferry",
  "lake-como/como-to-colico-ferry",
  "lake-como/colico-to-como-ferry",
]);

function titleFromKey(key: string) {
  return key
    .split("/")
    .at(-1)
    ?.split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "Lake Como page";
}

export function resolveLakeComoPageStudio(pathname: string): LakeComoPageStudioDefinition | null {
  const path = pathname.replace(/^\//, "").replace(/\/$/, "");
  if (path === gettingAround.pageKey) return gettingAround;
  if (!routeLikePaths.has(path)) return null;
  return {
    pageKey: path,
    title: titleFromKey(path),
    defaultImageQuery: "Lake Como Italy travel transport",
    capabilities: ["page", "faq", "editorialReview"],
    family: path.includes("ferry") ? "ferries" : path.includes("boat") ? "boat" : "route",
    canonicalPath: `/${path}`,
    sourceLocale: "en",
    localizable: true,
    localizedRendererReady: false,
    publicLocales: defaultPublicLocales,
    sitemapEligible: true,
    structuredDataType: "FAQPage",
    requiredTranslationSegments: ["hero.title", "hero.subtitle", ...baseTranslationSegments],
    optionalTranslationSegments: ["editorial_review.heading", "editorial_review.intro"],
    faqCapability: true,
    editorialReviewCapability: true,
    commercialCapability: true,
  };
}

/** Shared registry used by the central Lake Como FAQ and editorial-review managers. */
export const lakeComoPageRegistry: LakeComoPageStudioDefinition[] = [
  gettingAround,
  ...Array.from(routeLikePaths).map((pageKey) => resolveLakeComoPageStudio(pageKey)!).filter(Boolean),
  { pageKey: "transport/lake-como/ferries", title: "Lake Como ferry planner", defaultImageQuery: "Lake Como ferry timetable", capabilities: ["page", "faq", "editorialReview"], family: "ferries", canonicalPath: "/transport/lake-como/ferries", sourceLocale: "en", localizable: true, localizedRendererReady: true, publicLocales: ["en", "it"], sitemapEligible: true, structuredDataType: "WebPage", requiredTranslationSegments: ["hero.title", "hero.subtitle", ...baseTranslationSegments], optionalTranslationSegments: ["editorial_review.heading", "editorial_review.intro"], faqCapability: true, editorialReviewCapability: true, commercialCapability: false },
  { pageKey: "lake-como/boat-tours", title: "Lake Como boat tours", defaultImageQuery: "Lake Como boat tour", capabilities: ["page", "faq", "editorialReview"], family: "boat", canonicalPath: "/lake-como/boat-tours", sourceLocale: "en", localizable: true, localizedRendererReady: true, publicLocales: ["en", "it"], sitemapEligible: true, structuredDataType: "FAQPage", requiredTranslationSegments: ["hero.title", "hero.subtitle", ...baseTranslationSegments], optionalTranslationSegments: ["editorial_review.heading", "editorial_review.intro"], faqCapability: true, editorialReviewCapability: true, commercialCapability: true },
  { pageKey: "lake-como/shared-boat-tours", title: "Shared Lake Como boat tours", defaultImageQuery: "Lake Como shared boat tour", capabilities: ["page", "faq", "editorialReview"], family: "boat", canonicalPath: "/lake-como/shared-boat-tours", sourceLocale: "en", localizable: true, localizedRendererReady: true, publicLocales: ["en", "it"], sitemapEligible: true, structuredDataType: "FAQPage", requiredTranslationSegments: ["hero.title", "hero.subtitle", ...baseTranslationSegments], optionalTranslationSegments: [], faqCapability: true, editorialReviewCapability: true, commercialCapability: true },
  { pageKey: "lake-como/private-boat-tours", title: "Private Lake Como boat tours", defaultImageQuery: "Lake Como private boat tour", capabilities: ["page", "faq", "editorialReview"], family: "boat", canonicalPath: "/lake-como/private-boat-tours", sourceLocale: "en", localizable: true, localizedRendererReady: true, publicLocales: ["en", "it"], sitemapEligible: true, structuredDataType: "FAQPage", requiredTranslationSegments: ["hero.title", "hero.subtitle", ...baseTranslationSegments], optionalTranslationSegments: [], faqCapability: true, editorialReviewCapability: true, commercialCapability: true },
].filter((item, index, all) => all.findIndex((candidate) => candidate.pageKey === item.pageKey) === index) as LakeComoPageStudioDefinition[];

/** The one public-content registry used by editorial admin, localized URLs and future publication state. */
export function getLakeComoPageDefinition(pageKey: LakeComoPageKey): LakeComoPageStudioDefinition | null {
  return lakeComoPageRegistry.find((page) => page.pageKey === pageKey) || null;
}
